import {
    Card, CardContent, IconButton,
    Stack, Tooltip, Typography, Skeleton,
    CircularProgress, Container, TextField, InputAdornment
} from "@mui/material";
import Box from "@mui/material/Box";
import { Message } from "../types/courseTypes";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from '@mui/icons-material/Delete';
import CachedIcon from '@mui/icons-material/Cached';
import FileDownloadIcon from '@mui/icons-material/FileDownload';
import CopyButton from "./CopyButton";
import html2canvas from 'html2canvas';
import jsPDF from "jspdf";
import SAILLogo from '../SAIL.svg';
import React from "react";
import CheckIcon from "@mui/icons-material/Check";
import CloseIcon from '@mui/icons-material/Close';

interface MessageArrayProps {
    message: Message;
    deleteMessage: (id: number) => void;
    handleUpdateMessage: (id: number, inputText?:string) => void;
    loading?: boolean;
    disableEditDelete?: boolean;
}

const MessageArray: React.FC<MessageArrayProps> = ({ message, deleteMessage, handleUpdateMessage, loading, disableEditDelete = false }) => {
    const [isEditing, setIsEditing] = React.useState(false);
    const [editedText, setEditedText] = React.useState(message.inputText);

    const editMessage = () => {
        setIsEditing(true);
        setEditedText(message.inputText); // Initialize with current inputText
    };

    const handleConfirmEdit = () => {
        if (editedText.trim() && editedText !== message.inputText) {
            handleUpdateMessage(message.id, editedText); // Call API to update message
        }
        setIsEditing(false); // Exit editing mode
    };

    const handleCancelEdit = () => {
        setIsEditing(false); // Discard changes and exit editing mode
        setEditedText(message.inputText); // Reset to original text
    };

    // Process responseText to handle **bold** and ### headings with different sizes
    const responseMessage = message.responseText
        .replace(/\*\*(.*?)\*\*/g, '<strong style="font-size: 1.1em;">$1</strong>')
        .replace(/###\s*(.*?)(?=\n|$)/g, '<strong style="font-size: 1.25em; font-weight: bold;">$1</strong>');

    const handleExportToPDF = () => {
        const input = document.getElementById(`response-${message.id}`);
        if (!input) {
            console.error('Element not found for PDF export');
            return;
        }

        html2canvas(input, { scale: 2 }).then(canvas => {
            const imgData = canvas.toDataURL('image/png');
            const doc = new jsPDF({
                orientation: 'portrait',
                unit: 'mm',
                format: 'a4'
            });
            const imgProps = doc.getImageProperties(imgData);
            const pdfWidth = doc.internal.pageSize.getWidth();
            const pdfHeight = (imgProps.height * pdfWidth) / imgProps.width;
            let position = 10;

            doc.addImage(imgData, 'PNG', 10, position, pdfWidth - 20, pdfHeight);
            if (pdfHeight > doc.internal.pageSize.getHeight() - 20) {
                doc.addPage();
                position = 10;
                doc.addImage(imgData, 'PNG', 10, position, pdfWidth - 20, pdfHeight);
            }

            doc.save(`response_${message.id}.pdf`);
        });
    };

    const handleRegenrateResponse = () => {
        handleUpdateMessage(message.id);
    }

    // @ts-ignore
    return (
        <Container maxWidth="md">
            {/* User Message */}
            <Box sx={{ display: "flex", justifyContent: "center", width: "100%" }}>
                <Typography
                    variant="caption"
                    color="text.secondary"
                    sx={{ mb: 0.5 }}
                >
                    {new Date(message.createdAt).toLocaleString('en-NZ', { dateStyle: 'short', timeStyle: 'short' })}
                </Typography>
            </Box>
            <Box
                sx={{
                    alignSelf: 'flex-end',
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'flex-end',
                    '&:hover .hover-buttons': { opacity: 1 },
                }}
            >
                <Card
                    sx={{
                        backgroundColor: 'background.secondaryPaper',
                        borderRadius: '20px 20px 0 20px',
                    }}
                    elevation={1}
                >
                    <CardContent>
                        {isEditing ? (
                            <TextField
                                fullWidth
                                multiline
                                minRows={1}
                                maxRows={5}
                                value={editedText}
                                onChange={(e) => setEditedText(e.target.value)}
                                variant="standard"
                                slotProps={{
                                    input: {
                                        endAdornment: (
                                            <InputAdornment position="end">
                                                <IconButton
                                                    size="small"
                                                    onClick={handleConfirmEdit}
                                                    disabled={!editedText.trim() || loading}
                                                    color="success"
                                                >
                                                    <CheckIcon fontSize="small" />
                                                </IconButton>
                                                <IconButton
                                                    size="small"
                                                    onClick={handleCancelEdit}
                                                    disabled={loading}
                                                    color="error"
                                                >
                                                    <CloseIcon fontSize="small" />
                                                </IconButton>
                                            </InputAdornment>
                                        ),
                                    },
                                }}
                            />
                        ) : (
                            <Typography variant="body1">{message.inputText}</Typography>
                        )}
                    </CardContent>
                </Card>
                <Stack
                    className="hover-buttons"
                    direction="row"
                    spacing={1}
                    sx={{
                        mt: 1,
                        opacity: 0,
                        transition: 'opacity 0.2s',
                    }}
                >
                    <CopyButton textToCopy={message.inputText} />
                    { !disableEditDelete &&
                        <Tooltip title="Edit">
                            <IconButton size="small" onClick={() => editMessage()} disabled={disableEditDelete}>
                                <EditIcon fontSize="small" />
                            </IconButton>
                        </Tooltip>
                    }
                    { !disableEditDelete &&
                        <Tooltip title="Edit">
                            <IconButton size="small" onClick={() => deleteMessage(message.id)} disabled={disableEditDelete}>
                                <DeleteIcon fontSize="small" />
                            </IconButton>
                        </Tooltip>
                    }
                </Stack>
            </Box>
            {/* AI Response */}
            <Box
                sx={{
                    alignSelf: 'flex-start',
                    textAlign: 'left',
                    mb: 10,
                    width: '100%',
                    position: 'relative', // Allow logo to extend outside
                    overflow: 'visible', // Prevent clipping of logo
                }}
            >
                <Stack
                    direction="row"
                    spacing={2}
                    sx={{
                        alignItems: 'flex-start',
                    }}
                >
                    {/* SAIL Logo with Spinning CircularProgress */}
                    <Box
                        sx={{
                            position: 'absolute', // Allow logo to extend left
                            left: -40,
                            top: 0,
                            width: '100%',
                            maxWidth: 35,
                            zIndex: 1,
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                        }}
                    >
                        <Box sx={{position: 'relative' }}>
                            <img
                                src={SAILLogo}
                                alt="SAIL Logo"
                                style={{ width: 40, height: 'auto',
                                    transition: 'transform 0.5s ease',
                                    ...(loading ? {
                                        transform: 'scale(0.8)', // Shrink to 80% during loading
                                        animation: 'shrink 0.5s ease-in',
                                        '@keyframes shrink': {
                                            '0%': { transform: 'scale(1)' },
                                            '100%': { transform: 'scale(0.8)' },
                                        },
                                    } : {
                                        transform: 'scale(1)', // Grow back to 100% after loading
                                        animation: 'grow 0.5s ease-out',
                                        '@keyframes grow': {
                                            '0%': { transform: 'scale(0.8)' },
                                            '100%': { transform: 'scale(1)' },
                                        },
                                    }),
                                }}
                            />
                            {loading && (
                                <React.Fragment>
                                    <svg width={0} height={0}>
                                        <defs>
                                            <linearGradient id="my_gradient" x1="0%" y1="0%" x2="0%" y2="100%">
                                                <stop offset="0%" stopColor="grey"/>
                                                <stop offset="100%" stopColor="black" />
                                            </linearGradient>
                                        </defs>
                                    </svg>
                                    <CircularProgress
                                        size={50}
                                        sx={{
                                            'svg circle': { stroke: 'url(#my_gradient)' },
                                            color: 'inherit',
                                            position: 'absolute',
                                            top: -4,
                                            left: -5,
                                            zIndex: 1,
                                        }}
                                    />
                                </React.Fragment>
                            )}
                        </Box>
                    </Box>
                    {/* Response Content */}
                    <Box sx={{ flex: 1, ml: 6 }}> {/* Margin to prevent overlap with logo */}
                        {loading ? (
                            <Box sx={{ position: 'relative', width: '100%' }}>
                                <Stack>
                                    <Skeleton
                                        variant="text"
                                        animation="wave"
                                        height={"40"}
                                        sx={{
                                            mt: 1,
                                            ml: 25,
                                        }}
                                    />
                                    <Skeleton
                                        variant="text"
                                        animation="wave"
                                        height={"40"}
                                        sx={{
                                            mt: 1,
                                        }}
                                    />
                                    <Skeleton
                                        variant="text"
                                        animation="wave"
                                        height={"40"}
                                        sx={{
                                            mt: 1,
                                        }}
                                    />
                                    <Skeleton
                                        variant="text"
                                        animation="wave"
                                        height={"40"}
                                        sx={{
                                            mt: 1,
                                        }}
                                    />
                                </Stack>
                                <Typography
                                    variant="subtitle2"
                                    fontSize="1rem"
                                    sx={{
                                        position: 'absolute',
                                        top: 5,
                                        left: 10,
                                    }}
                                >
                                    Fetching Response...
                                </Typography>
                            </Box>
                        ) : (
                            <>
                                <Typography
                                    id={`response-${message.id}`}
                                    variant="body1"
                                    component="div"
                                    sx={{ whiteSpace: 'pre-wrap' }}
                                    dangerouslySetInnerHTML={{
                                        __html: responseMessage,
                                    }}
                                />
                                <Stack
                                    direction="row"
                                    spacing={1}
                                    sx={{
                                        mt: 1,
                                    }}
                                >
                                    <Tooltip title="Regenerate">
                                        <IconButton size="small" onClick={handleRegenrateResponse} disabled={loading}>
                                            <CachedIcon fontSize="small" />
                                        </IconButton>
                                    </Tooltip>
                                    <CopyButton textToCopy={responseMessage} disabled={loading} />
                                    <Tooltip title="Export to PDF">
                                        <IconButton size="small" onClick={handleExportToPDF} disabled={loading}>
                                            <FileDownloadIcon fontSize="small" />
                                        </IconButton>
                                    </Tooltip>
                                </Stack>
                            </>
                        )}
                    </Box>
                </Stack>
            </Box>
        </Container>
    );
};

export default MessageArray;

function handleUpdateMessage(id: number) {
    throw new Error("Function not implemented.");
}
